import React, { useCallback, useRef, useState } from 'react';
import {
  AUTH_EVENTS,
  getSessionReadyFromSso,
  useSsoOptional,
  type SsoContextValue,
} from '@discover-frontend/auth';

import type { SessionGatedLinkProps } from './types';
import { waitForSessionReady } from './waitForSessionReady';

const SessionGatedLink = ({
  href,
  onNavigate,
  children,
  target = '_self',
  restoringLabel,
  ...rest
}: SessionGatedLinkProps) => {
  const sso = useSsoOptional();
  const ssoRef = useRef<SsoContextValue | null | undefined>(sso);
  ssoRef.current = sso;
  const [isRestoring, setIsRestoring] = useState(false);

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const currentSso = ssoRef.current;

      if (currentSso?.sessionPhase === 'locked') {
        return;
      }

      if (getSessionReadyFromSso(currentSso)) {
        onNavigate?.();
        window.location.assign(href);
        return;
      }

      setIsRestoring(true);

      try {
        if (!ssoRef.current?.isRefreshing) {
          window.dispatchEvent(new Event(AUTH_EVENTS.AUTH_FAILURE));
        }

        await waitForSessionReady(() => ssoRef.current);
        onNavigate?.();
        window.location.assign(href);
      } catch {
        // Locked, timed out, or refresh failed — stay on the current page.
      } finally {
        setIsRestoring(false);
      }
    },
    [href, onNavigate]
  );

  return (
    <a
      href={href}
      target={target}
      onClick={handleClick}
      aria-busy={isRestoring}
      aria-label={isRestoring && restoringLabel ? restoringLabel : undefined}
      style={isRestoring ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
      {...rest}
    >
      {children}
    </a>
  );
};

export default SessionGatedLink;
